/**
 * Custom element pour la recherche prédictive Shopify.
 *
 * Fonctionnalités :
 * - Recherche en temps réel avec debouncing (300ms)
 * - localStorage pour l'historique (expiration 24h)
 * - Intégration API Shopify Predictive Search avec Section Rendering
 * - Navigation clavier (Flèches, Entrée, Échap)
 * - Accessibilité (ARIA)
 * - Design responsive
 */

class CPredictiveSearch extends HTMLElement {
  constructor() {
    super()

    // Configuration
    this.DEBOUNCE_DELAY = 300
    this.MIN_SEARCH_LENGTH = 3
    this.PREDICTIVE_SEARCH_API_LIMIT = 10 // Limite de l'API Shopify Predictive Search
    this.HISTORY_KEY = 'barriere_search_history'
    this.HISTORY_MAX_ITEMS = 5
    this.HISTORY_EXPIRATION = 24 * 60 * 60 * 1000 // 24h

    // Références DOM
    this.modal = this.querySelector('.predictive-search')
    this.modalContent = this.querySelector('.predictive-search__modal')
    this.overlay = this.querySelector('[data-overlay]')
    this.input = this.querySelector('[data-search-input]')
    this.predictiveSearchResults = this.querySelector('#predictive-search')
    this.closeBtn = this.querySelector('[data-close-search]')
    this.swipeHandle = this.querySelector('[data-swipe-handle]')

    // États
    this.initialState = this.querySelector('[data-initial-state]')
    this.resultsState = this.querySelector('[data-results-state]')

    // État initial
    this.historyContainer = this.querySelector('[data-search-history]')
    this.historyList = this.querySelector('[data-history-list]')
    this.initialLists = this.querySelectorAll('[data-initial-list]')

    // État résultats
    this.suggestionsWrapper = this.querySelector(
      '.predictive-search__suggestions'
    )
    this.suggestionsList = this.querySelector('[data-suggestions-list]')
    this.productsWrapper = this.querySelector(
      '.predictive-search__products-wrapper'
    )
    this.productsGrid = this.querySelector('[data-products-grid]')
    this.loading = this.querySelector('[data-loading]')
    this.cta = this.querySelector('[data-cta]')
    this.seeAllLink = this.querySelector('[data-see-all-link]')
    this.noResults = this.querySelector('[data-no-results]')
    this.resultsCount = this.querySelector('[data-results-count]')
    this.countValue = this.querySelector('[data-count-value]')
    this.searchStatus = this.querySelector('[data-search-status]')

    // État
    this.isOpen = false
    this.debounceTimer = null
    this.currentQuery = ''
    this.abortController = null

    // État swipe (mobile)
    this.touchStartY = 0
    this.touchCurrentY = 0
    this.isDragging = false
    this.SWIPE_THRESHOLD = 100 // Distance minimale pour fermer (px)
  }

  connectedCallback() {
    this.init()
  }

  disconnectedCallback() {
    // Nettoyer le timer de debounce
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }

    // Annuler toute requête en cours
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }

    // Réinitialiser l'overflow du body si le modal était ouvert
    if (this.isOpen) {
      document.body.style.overflow = ''
    }
  }

  init() {
    this.bindEvents()
    this.loadSearchHistory()
  }

  bindEvents() {
    // Boutons avec data-toggle-predictive-search
    document
      .querySelectorAll('[data-toggle-predictive-search]')
      .forEach((btn) => {
        btn.addEventListener('click', () => this.open())
      })

    // Boutons de recherche dans le header
    document.querySelectorAll('.btn-search').forEach((btn) => {
      btn.addEventListener('click', () => this.open())
    })

    // Fermeture
    this.closeBtn?.addEventListener('click', () => this.close())
    this.overlay?.addEventListener('click', () => this.close())

    // Input
    this.input?.addEventListener('input', (e) => this.handleInput(e))
    this.input?.addEventListener('keydown', (e) => this.handleKeydown(e))

    // Pills de recherche
    this.querySelectorAll('[data-search-term]').forEach((pill) => {
      pill.addEventListener('click', (e) => {
        const term = e.currentTarget.dataset.searchTerm
        this.performSearch(term)
      })
    })

    // Échap pour fermer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close()
      }
    })

    // Swipe pour fermer (mobile)
    this.modalContent?.addEventListener('touchstart', (e) =>
      this.handleTouchStart(e)
    )
    this.modalContent?.addEventListener('touchmove', (e) =>
      this.handleTouchMove(e)
    )
    this.modalContent?.addEventListener('touchend', () => this.handleTouchEnd())
  }

  /**
   * Ouvre le modal
   */
  open() {
    this.isOpen = true
    this.modal.classList.add('is-open')
    document.body.style.overflow = 'hidden'

    // Cacher le bouton sticky quand la recherche est ouverte
    const stickyBtn = document.querySelector(
      '.section-btn-sticky:has(.v-mobile)'
    )
    if (stickyBtn) {
      stickyBtn.classList.add('is-hide')
    }

    // Vider le champ de recherche à chaque ouverture
    if (this.input) {
      this.input.value = ''
    }
    this.currentQuery = ''

    setTimeout(() => {
      this.input?.focus()
    }, 100)

    this.showInitialState()

    if (this.input) {
      this.input.setAttribute('aria-expanded', 'true')
    }
  }

  /**
   * Ferme le modal
   */
  close() {
    this.isOpen = false
    this.modal.classList.remove('is-open')
    document.body.style.overflow = ''

    // Réafficher le bouton sticky quand la recherche est fermée
    const stickyBtn = document.querySelector(
      '.section-btn-sticky:has(.v-mobile)'
    )
    if (stickyBtn) {
      stickyBtn.classList.remove('is-hide')
    }

    this.input.value = ''
    this.currentQuery = ''
    this.showInitialState()

    // Réinitialiser la position du modal (swipe)
    this.modalContent.style.transform = ''

    if (this.input) {
      this.input.setAttribute('aria-expanded', 'false')
    }
  }

  /**
   * Gère l'input avec debounce
   */
  handleInput(event) {
    const query = event.target.value.trim()

    clearTimeout(this.debounceTimer)

    if (query.length === 0) {
      this.showInitialState()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.performSearch(query)
    }, this.DEBOUNCE_DELAY)
  }

  /**
   * Gère la navigation clavier
   */
  handleKeydown(event) {
    const { key } = event

    if (key === 'Enter') {
      event.preventDefault()
      const query = this.input.value.trim()
      if (query.length >= this.MIN_SEARCH_LENGTH) {
        this.goToSearchPage(query)
      }
    }
  }

  /**
   * Gère le début du swipe
   */
  handleTouchStart(event) {
    // Ne gérer le swipe que si on touche le handle ou le haut de la modale
    const target = event.target
    const isHandle = target.closest('[data-swipe-handle]')
    const isHeader = target.closest('.predictive-search__header')

    if (isHandle || isHeader) {
      this.touchStartY = event.touches[0].clientY
      this.isDragging = true
      this.modalContent.style.transition = 'none'
    }
  }

  /**
   * Gère le mouvement du swipe
   */
  handleTouchMove(event) {
    if (!this.isDragging) return

    this.touchCurrentY = event.touches[0].clientY
    const deltaY = this.touchCurrentY - this.touchStartY

    // Ne permettre que le swipe vers le bas
    if (deltaY > 0) {
      event.preventDefault()
      this.modalContent.style.transform = `translateY(${deltaY}px)`
    }
  }

  /**
   * Gère la fin du swipe
   */
  handleTouchEnd() {
    if (!this.isDragging) return

    const deltaY = this.touchCurrentY - this.touchStartY

    // Réactiver la transition
    this.modalContent.style.transition = ''

    // Fermer si le swipe dépasse le seuil
    if (deltaY > this.SWIPE_THRESHOLD) {
      this.close()
    } else {
      // Sinon, revenir à la position initiale
      this.modalContent.style.transform = ''
    }

    // Réinitialiser l'état
    this.isDragging = false
    this.touchStartY = 0
    this.touchCurrentY = 0
  }

  /**
   * Effectue une recherche via l'API Predictive Search
   */
  async performSearch(query) {
    this.currentQuery = query
    this.input.value = query

    // Afficher le loading
    this.showLoading()

    // Créer AbortController pour annuler les requêtes précédentes
    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()

    // Construire l'URL selon la longueur de la requête
    let apiUrl =
      window.Shopify.routes.root +
      `search/suggest?q=${encodeURIComponent(query)}`

    // Toujours rechercher les suggestions (queries)
    apiUrl += '&resources[type]=query'

    // À partir de 3 caractères, rechercher aussi les produits et en fonction de tous leurs champs
    if (query.length >= this.MIN_SEARCH_LENGTH) {
      apiUrl +=
        '&resources[type]=product&resources[options][fields]=body,tag,title,variants.title'
    }

    apiUrl += '&section_id=predictive-search-results'

    // Faire la requête à l'API Predictive Search
    fetch(apiUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status)
        }
        return response.text()
      })
      .then((predictiveText) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(predictiveText, 'text/html')
          .querySelector('#shopify-section-predictive-search-results')

        if (resultsMarkup) {
          this.predictiveSearchResults.innerHTML = resultsMarkup.innerHTML
        } else {
          console.error(
            'Could not find #shopify-section-predictive-search-results in response'
          )
        }

        // Afficher les résultats
        this.displayResults()
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return // Requête annulée, ne rien faire
        }
        console.error('Predictive search error:', error)
        this.showNoResults()
      })
  }

  /**
   * Affiche l'état de chargement
   */
  showLoading() {
    this.initialState.hidden = true
    this.resultsState.hidden = false
    this.loading.hidden = false

    // Marquer comme en cours de chargement pour l'accessibilité
    if (this.input) {
      this.input.setAttribute('aria-busy', 'true')
    }
  }

  /**
   * Affiche l'état initial
   */
  showInitialState() {
    this.initialState.hidden = false
    this.resultsState.hidden = true
    this.loadSearchHistory()
  }

  /**
   * Affiche les résultats de recherche
   */
  displayResults() {
    this.loading.hidden = true
    this.initialState.hidden = true
    this.resultsState.hidden = false

    // Marquer le chargement comme terminé
    if (this.input) {
      this.input.setAttribute('aria-busy', 'false')
    }

    // Afficher suggestions et produits
    const suggestions =
      this.predictiveSearchResults.querySelector('[data-suggestions]')
    const products =
      this.predictiveSearchResults.querySelector('[data-products]')

    // Gérer l'affichage des suggestions
    // Si l'API retourne des suggestions, les afficher
    // Sinon, garder les best_suggestions configurées (déjà dans le HTML)
    if (suggestions) {
      const suggestionsList = suggestions.querySelector('ul')
      if (suggestionsList) {
        this.suggestionsList.innerHTML = suggestionsList.innerHTML
      }
    }
    // Toujours afficher le wrapper (contient soit les suggestions de l'API, soit les best_suggestions)
    this.suggestionsWrapper.hidden = false

    // Gérer l'affichage des produits et du message "Aucun résultat"
    // Seulement si on a tapé 3+ caractères
    if (this.currentQuery.length >= this.MIN_SEARCH_LENGTH) {
      // Montrer le wrapper des produits (contient aussi le message "Aucun résultat")
      this.productsWrapper.hidden = false

      if (products) {
        const productsGrid = products.querySelector(
          '.predictive-search-results__products-grid'
        )

        if (productsGrid) {
          this.productsGrid.innerHTML = productsGrid.innerHTML
        }

        // Récupérer le nombre total de résultats retournés par l'API
        const totalResults = parseInt(
          products.getAttribute('data-total-results') || '0'
        )
        const displayedCount =
          this.productsGrid.querySelectorAll('.card-product').length

        // Toujours afficher le compteur avec le nombre de résultats
        if (displayedCount > 0) {
          // Si l'API retourne 10 résultats (limite API), afficher "10+" car il y a potentiellement plus
          if (totalResults === this.PREDICTIVE_SEARCH_API_LIMIT) {
            this.countValue.textContent = '10+'
            this.announceStatus(`${displayedCount}+ résultats affichés`)
          } else {
            // Sinon, afficher le nombre exact
            this.countValue.textContent = displayedCount
            if (displayedCount === 1) {
              this.announceStatus(`1 résultat trouvé`)
            } else {
              this.announceStatus(`${displayedCount} résultats trouvés`)
            }
          }
          this.resultsCount.hidden = false
        }

        // Afficher le bouton "Voir tous..." uniquement si limite API atteinte (10 résultats)
        this.seeAllLink.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(this.currentQuery)}`
        this.cta.hidden = totalResults !== this.PREDICTIVE_SEARCH_API_LIMIT

        // Montrer les produits, cacher "Aucun résultat"
        this.productsGrid.hidden = false
        this.noResults.hidden = true
      } else {
        // Pas de produits trouvés : cacher la grille, afficher "Aucun résultat"
        this.productsGrid.hidden = true
        this.resultsCount.hidden = true
        this.cta.hidden = true
        this.noResults.hidden = false

        // Annoncer l'absence de résultats
        this.announceStatus('Aucun résultat trouvé')
      }
    } else {
      // Moins de 3 caractères : cacher complètement le wrapper des produits
      this.productsWrapper.hidden = true
      this.resultsCount.hidden = true

      // Annoncer les suggestions disponibles
      if (suggestions) {
        const suggestionsCount = suggestions.querySelectorAll('li').length
        if (suggestionsCount > 0) {
          this.announceStatus(
            `${suggestionsCount} suggestion${suggestionsCount > 1 ? 's' : ''} disponible${suggestionsCount > 1 ? 's' : ''}`
          )
        }
      }
    }
  }

  /**
   * Annonce un message pour les lecteurs d'écran
   */
  announceStatus(message) {
    if (this.searchStatus) {
      this.searchStatus.textContent = message
    }
  }

  /**
   * Affiche "aucun résultat"
   */
  showNoResults() {
    this.loading.hidden = true
    this.initialState.hidden = true
    this.resultsState.hidden = false
    this.suggestionsWrapper.hidden = true
    this.productsWrapper.hidden = true
    this.noResults.hidden = false
  }

  /**
   * Charge l'historique depuis localStorage
   */
  loadSearchHistory() {
    const history = this.getSearchHistory()

    if (history.length > 0) {
      this.historyContainer.hidden = false
      this.initialLists.forEach((list) => list.setAttribute('hidden', ''))

      const html = history
        .map(
          (item) => `
        <li role="option">
          <button
            type="button"
            class="predictive-search__history-item body-m-base"
            data-search-term="${this.escapeHtml(item.query)}"
          >
            ${this.escapeHtml(item.query)}
          </button>
        </li>
      `
        )
        .join('')

      this.historyList.innerHTML = html

      this.historyList.querySelectorAll('[data-search-term]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const term = e.currentTarget.dataset.searchTerm
          this.performSearch(term)
        })
      })
    } else {
      this.historyContainer.hidden = true
      this.initialLists.forEach((list) => list.removeAttribute('hidden'))
    }
  }

  /**
   * Récupère l'historique avec expiration 24h
   */
  getSearchHistory() {
    try {
      const stored = localStorage.getItem(this.HISTORY_KEY)
      if (!stored) return []

      const history = JSON.parse(stored)
      const now = Date.now()

      const validHistory = history.filter((item) => {
        return now - item.timestamp < this.HISTORY_EXPIRATION
      })

      if (validHistory.length !== history.length) {
        localStorage.setItem(this.HISTORY_KEY, JSON.stringify(validHistory))
      }

      return validHistory
    } catch (error) {
      console.error('Error loading search history:', error)
      return []
    }
  }

  /**
   * Sauvegarde dans l'historique
   */
  saveToHistory(query) {
    try {
      const history = this.getSearchHistory()

      const existingIndex = history.findIndex(
        (item) => item.query.toLowerCase() === query.toLowerCase()
      )

      if (existingIndex > -1) {
        history.splice(existingIndex, 1)
      }

      history.unshift({
        query: query,
        timestamp: Date.now(),
      })

      const trimmedHistory = history.slice(0, this.HISTORY_MAX_ITEMS)

      localStorage.setItem(this.HISTORY_KEY, JSON.stringify(trimmedHistory))
    } catch (error) {
      console.error('Error saving search history:', error)
    }
  }

  /**
   * Redirige vers la page de recherche
   */
  goToSearchPage(query) {
    this.saveToHistory(query)
    window.location.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(query)}`
  }

  /**
   * Échappe les caractères HTML
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}

customElements.define('c-predictive-search', CPredictiveSearch)
